"use client";
import { Icon } from "@zapier/zinnia-icons";
import { BetaCarousel, Divider, Link, Text } from "@zapier/design-system";
import { ZapCard } from "../ZapCard/ZapCard";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import styles from "./TemplateDetailPage.module.css";
import { Markdown } from "../Markdown";
import { getBreadcrumbs } from "./constants";
import { TemplateDetailPageHeader } from "./TemplateDetailPageHeader";
import { BreadCrumbs } from "../BreadCrumbs/BreadCrumbs";
import { CanvasEmbed } from "../CanvasEmbed/CanvasEmbed";
import { FAQSchema, type TemplateFAQ } from "./FAQSchema";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import {
  type Template,
  type ZapBreakdownDetails,
  AppsSection,
  CreatedAssetsSection,
  TemplateCTAs,
  Author,
  useTemplateBookmarkActions,
  ZapBreakdown,
  ZapBreakdownSkeleton,
} from "@zapier/templates";
import { useTemplateExecutionContext } from "src/contexts/TemplateExecutionContext";
import { useSessionContext } from "src/contexts/SessionContext";
import { getZapBreakdown } from "src/lib/getZapBreakdown";
import { isZapTemplate } from "src/utils/isZapTemplate";

type TemplateDetailPageProps = {
  template: Template;
  relatedTemplates: Template[];
  isDraftModeEnabled: boolean;
};

export const TemplateDetailPage = ({
  template,
  relatedTemplates,
  isDraftModeEnabled,
}: TemplateDetailPageProps) => {
  const { session } = useSessionContext();
  const isLoggedIn = session.is_logged_in;
  const t = useTranslations("TemplateDetail");

  const bookmark = useTemplateBookmarkActions(template.id);

  const {
    createTemplate,
    creationScreen,
    error: creationError,
    setError: setCreationError,
  } = useTemplateExecutionContext();

  const tryTemplateActions = {
    hasCreationScreen: !!creationScreen,
    createTemplate: createTemplate,
    creationError: creationError,
    setCreationError: setCreationError,
  };

  // Fetch Zap breakdown details for Zap templates
  const shouldLoadZapBreakdown = isZapTemplate(template);
  const [zapBreakdownDetails, setZapBreakdownDetails] =
    useState<ZapBreakdownDetails | null>(template.zapBreakdownDetails ?? null);
  const [isLoadingZapBreakdown, setIsLoadingZapBreakdown] = useState(
    shouldLoadZapBreakdown && !template.zapBreakdownDetails,
  );

  useEffect(() => {
    const fetchZapBreakdown = async () => {
      try {
        setIsLoadingZapBreakdown(true);
        const details = await getZapBreakdown(template);
        setZapBreakdownDetails(details);
      } catch (error) {
        console.error("Error fetching zap breakdown:", error);
      } finally {
        setIsLoadingZapBreakdown(false);
      }
    };

    if (!zapBreakdownDetails && shouldLoadZapBreakdown) {
      fetchZapBreakdown();
    }
  }, [template, zapBreakdownDetails, shouldLoadZapBreakdown]);

  // Show Zap breakdown in workflow preview section if no canvas URL exists
  const showZapBreakdownSection = !template.canvasUrl && shouldLoadZapBreakdown;
  const zapBreakdownSection = showZapBreakdownSection ? (
    <section className={styles.hiddenOnMobile}>
      <Text type="SectionHeader" tag="h2" margin="0 0 12px 0">
        {t("workflowPreview")}
      </Text>
      {isLoadingZapBreakdown ? (
        <ZapBreakdownSkeleton />
      ) : zapBreakdownDetails ? (
        <ZapBreakdown
          title={zapBreakdownDetails.title}
          firstStep={zapBreakdownDetails.firstStep}
          lastStep={zapBreakdownDetails.lastStep}
          totalStepCount={zapBreakdownDetails.totalStepCount}
          services={zapBreakdownDetails.services}
          servicesList={zapBreakdownDetails.servicesList}
        />
      ) : null}
    </section>
  ) : null;

  const canvasEmbedSection = template.canvasUrl ? (
    <section className={styles.hiddenOnMobile}>
      <div className={styles.canvasTitleSection}>
        <Text type="SectionHeader" tag="h2">
          {t("workflowPreview")}
        </Text>
        <Link
          variant="tertiary"
          href={template.canvasUrl}
          onPress={() => {
            emitUserInteractionEvent(
              UserInteractionEventName.openInCanvasButtonClicked,
              {
                event_action: "click",
                option_selected: template.slug,
                custom_tracking_fields: {
                  template_id: template.id,
                },
              },
            );
          }}
          iconPosition="start"
        >
          <Icon name="navCanvas" />
          {t("openInCanvas")}
        </Link>
      </div>
      <Text type="Body1Multiline" margin="0 0 12px 0" tag="p">
        {t("canvasInstructions")}
      </Text>
      <CanvasEmbed
        url={template.canvasUrl}
        showOpenInCanvasButton={false}
        className={styles.canvasEmbed}
      />
    </section>
  ) : null;

  const overviewSection = template.content ? (
    <section>
      <Text type="SectionHeader" tag="h2">
        {t("overview")}
      </Text>
      <Markdown content={template.contentAsMarkdown ?? ""} />
    </section>
  ) : null;

  const validFaqs = template.faqs?.filter((faq) => faq !== null) ?? [];

  const faqSection = validFaqs && validFaqs.length > 0 && (
    <section>
      <Text type="SectionHeader" tag="h2">
        {t("faq")}
      </Text>
      <div className={styles.faqs}>
        {validFaqs?.map((faq: TemplateFAQ) => (
          <details key={faq.name} className={styles.faqItem}>
            <summary className={styles.faqItemSummary}>
              <Text type="ParagraphHeader3">{faq.name}</Text>
              <div className={styles.faqItemIcon}>
                <Icon name="arrowSmallDown" size={24} isBlock />
              </div>
            </summary>
            <Markdown content={faq.content} />
          </details>
        ))}
      </div>
    </section>
  );

  const ctaSection = !isLoggedIn && (
    <section className={styles.ctaBanner}>
      <Text tag="h2" type="PageHeader" className={styles.title}>
        {template.name}
      </Text>
      <TemplateCTAs
        template={template}
        isLoggedIn={isLoggedIn}
        align="center"
        hasBookmarking={isLoggedIn}
        templateBookmarkActions={bookmark}
        tryTemplateActions={tryTemplateActions}
        hasTemplatePage={true}
      />
    </section>
  );

  const relatedTemplatesSection = relatedTemplates.length > 0 && (
    <section>
      <Text type="SectionHeader" tag="h2" margin="0 0 12px 0">
        {t("relatedTemplates")}
      </Text>
      <div className={styles.relatedTemplates}>
        <BetaCarousel
          className={styles.carouselWrapper}
          paddingBottom={30}
          pageGutterWidth={0}
        >
          {relatedTemplates.map((template) => (
            <ZapCard
              key={template.slug}
              template={template}
              href={`/templates/details/${template.slug}`}
            />
          ))}
        </BetaCarousel>
      </div>
    </section>
  );

  const hasMainContent =
    zapBreakdownSection ||
    canvasEmbedSection ||
    overviewSection ||
    faqSection ||
    ctaSection ||
    relatedTemplatesSection;

  const hasValidFaqs = validFaqs.length > 0;

  return (
    <div className={styles.page}>
      {!isLoggedIn && hasValidFaqs && (
        <FAQSchema questions={validFaqs ?? null} />
      )}
      <BreadCrumbs crumbs={getBreadcrumbs(template)} />
      <TemplateDetailPageHeader
        template={template}
        isLoggedIn={isLoggedIn}
        isDraftModeEnabled={isDraftModeEnabled}
      />
      <Divider height={1} width="full" />
      <div className={styles.layout}>
        {hasMainContent && (
          <div className={styles.mainContent}>
            {zapBreakdownSection}
            {canvasEmbedSection}
            {overviewSection}
            {faqSection}
            {ctaSection}
            {relatedTemplatesSection}
          </div>
        )}
        <aside className={styles.sidebar}>
          {template.author && (
            <Author author={template.author} label={t("createdBy")} />
          )}
          <CreatedAssetsSection template={template} />
          <AppsSection template={template} />
        </aside>
      </div>
    </div>
  );
};
